import {AfterViewChecked, Component, DoCheck, ElementRef, EventEmitter, Input, OnChanges,
  OnDestroy, Output, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs/Subscription';
import {UserService} from 'app/services/user.service';
import {GradePromotionService} from 'app/services/grade-promotion.service';
import {DealPayrollGradeCode} from 'app/models/deal-payroll.model';
import {dealPayrollGradeKey} from 'app/models/deal-payroll-presentation';
import {GradeGrowthEvaluation, GradePromotionEvent, GradePromotionResponse, GradePromotionSummary, GrowthLearningPreview} from './grade-growth.model';
import {GrowthEvaluationView, growthEvaluationView, trackGrowthLearningCount} from './grade-growth-presentation';
import {GrowthPromotionView, growthAutomationKey, growthOperationView, growthPromotionErrorKey,
  growthPromotionView} from './grade-promotion-presentation';

const COMMITTED = 'COMMITTED';
const APPLYING = 'APPLYING';
const AWAITING_CONFIRMATION = 'AWAITING_CONFIRMATION';
const ACTION = 'action';
const EMPTY = '';
const ZERO = 0;
const FORBIDDEN = 403;
const UNAUTHORIZED = 401;
const NOT_FOUND = 404;
const CONFLICT = 409;
const INVALID = 400;
const UNAVAILABLE = 503;
const SERVER_ERROR = 500;
const UNKNOWN_OUTCOME = 'GRADE_GROWTH_PROMOTION_OUTCOME_UNKNOWN';
const READONLY = 'readonly';
const STRING_TYPE = 'string';
const NUMBER_TYPE = 'number';
const HEAD_ROLE = 'ROLE_HEAD';
const ADMIN_ROLE = 'ROLE_ADMIN';
const CANCELLED = 'CANCELLED';
const REJECTED = 'REJECTED';
const LOADING_KEY = 'GRADE_GROWTH_PROMOTION_LOADING';
const SUBMITTING_KEY = 'GRADE_GROWTH_PROMOTION_SUBMITTING';
const COMMANDS_DISABLED_KEY = 'GRADE_GROWTH_REASON_COMMANDS_DISABLED';

@Component({selector: 'app-grade-promotion', templateUrl: './grade-promotion.component.html',
  styleUrls: ['./grade-promotion.component.css']})
export class GradePromotionComponent implements OnChanges, DoCheck, OnDestroy, AfterViewChecked {
  readonly trackLearningCount = trackGrowthLearningCount;
  @Input() userId: string;
  @Input() employeeId: string;
  @Input() operationId: string;
  @Input() mode: 'readonly' | 'action' = READONLY;
  @Input() compact = false;
  @Input() refreshToken = ZERO;
  @Input() fromGradeCode: string;
  @Input() toGradeCode: string;
  @Input() evaluation: GradeGrowthEvaluation;
  @Input() learningPreview: GrowthLearningPreview;
  @Input() summary: GradePromotionSummary;
  @Output() committed = new EventEmitter<GradePromotionEvent>();
  @Output() refreshRequested = new EventEmitter<GradePromotionEvent>();
  @ViewChild('status') status: ElementRef;
  modeKey: string;
  active: GrowthPromotionView;
  lastCommitted: GrowthPromotionView;
  conditions: GrowthEvaluationView;
  operation: GradePromotionResponse = null;
  loading = false;
  busy = false;
  outcomeUnknown = false;
  retryAllowed = false;
  errorKey = EMPTY;
  noticeKey = EMPTY;
  compactFromKey = EMPTY;
  compactToKey = EMPTY;
  private context = EMPTY;
  private generation = ZERO;
  private request: Subscription;
  private destroyed = false;
  private focusResult = false;
  private committedOperations: string[] = [];
  private resolvedOperations: string[] = [];
  private refreshedToken = ZERO;
  private refreshedRevision: number;
  private refreshPending = false;

  constructor(private promotions: GradePromotionService, private users: UserService) {}

  get headFlow(): boolean { return this.mode === ACTION || !!(this.evaluation && this.evaluation.applicationStatus); }
  get showConfirm(): boolean {
    return this.allowed() && (!this.operation || this.operation.state !== COMMITTED
      && this.operation.state !== CANCELLED && this.operation.state !== REJECTED);
  }
  get canConfirm(): boolean {
    return this.allowed() && !this.loading && !this.busy && !this.outcomeUnknown && !this.errorKey
      && !!this.operation && this.operation.state === AWAITING_CONFIRMATION
      && !!this.operation.capabilities && this.operation.capabilities.canConfirm === true
      && typeof this.operation.confirmationVersion === STRING_TYPE && !!this.operation.confirmationVersion.trim()
      && typeof this.operation.revision === NUMBER_TYPE && isFinite(this.operation.revision)
      && this.operation.revision >= ZERO && Math.floor(this.operation.revision) === this.operation.revision;
  }
  get canCheckStatus(): boolean {
    return this.allowed() && !this.busy && !this.loading && (this.retryAllowed || this.outcomeUnknown
      || !!this.operation && this.operation.state === APPLYING);
  }
  get compactReasonKey(): string {
    if (this.outcomeUnknown) { return UNKNOWN_OUTCOME; }
    if (this.busy) { return SUBMITTING_KEY; }
    if (this.loading) { return LOADING_KEY; }
    if (this.errorKey) { return this.errorKey; }
    if (this.canConfirm || !this.active || this.active.committed) { return EMPTY; }
    if (this.active.reasons.length) { return this.active.reasons[ZERO].labelKey; }
    return this.operation && this.operation.state === AWAITING_CONFIRMATION ? COMMANDS_DISABLED_KEY : this.active.stateKey;
  }

  ngOnChanges(): void { this.synchronize(); this.present(); }
  ngDoCheck(): void { this.synchronize(); }
  ngAfterViewChecked(): void {
    if (!this.focusResult || this.busy || this.loading || !this.status) { return; }
    this.focusResult = false; this.status.nativeElement.focus();
  }
  ngOnDestroy(): void { this.destroyed = true; this.cancel(); }

  checkStatus(): void {
    this.synchronize();
    if (this.canCheckStatus) { this.readOperation(); }
  }
  confirm(): void {
    this.synchronize();
    if (!this.canConfirm) { return; }
    const operation = this.operation;
    this.busy = true; this.retryAllowed = false; this.focusResult = true; this.noticeKey = EMPTY;
    const generation = ++this.generation;
    this.request = this.promotions.confirmPromotion(this.employee(), this.operationKey(), {
      expectedOperationRevision: operation.revision, confirmationVersion: operation.confirmationVersion
    }).subscribe(value => this.accept(value, generation, true), error => this.reject(error, generation, true));
  }
  private employee(): string { return this.employeeId || this.userId || EMPTY; }
  private operationKey(): string {
    return this.operationId || this.summary && this.summary.operationId
      || this.evaluation && this.evaluation.activeOperation && this.evaluation.activeOperation.operationId || EMPTY;
  }
  private allowed(): boolean {
    const viewer = this.users.getLocal();
    return this.mode === ACTION && !!viewer && !!viewer.id && viewer.id !== this.employee()
      && !!this.employee() && !!this.operationKey() && Array.isArray(viewer.roles)
      && (viewer.roles.indexOf(HEAD_ROLE) >= ZERO || viewer.roles.indexOf(ADMIN_ROLE) >= ZERO);
  }
  private currentContext(): string {
    const viewer = this.users.getLocal();
    return JSON.stringify([this.employee(), this.operationKey(), this.mode, viewer && viewer.id, this.allowed()]);
  }
  private synchronize(): void {
    if (this.destroyed) { return; }
    const context = this.currentContext();
    if (context === this.context) { this.refreshOperation(); return; }
    this.cancel(); this.context = context; this.operation = null;
    this.refreshedToken = this.refreshToken;
    this.refreshedRevision = this.summaryRevision();
    this.outcomeUnknown = false; this.retryAllowed = false; this.errorKey = EMPTY; this.noticeKey = EMPTY; this.focusResult = false;
    this.present();
    if (this.allowed()) { this.readOperation(); }
  }
  private refreshOperation(): void {
    if (this.refreshToken !== this.refreshedToken || this.summaryRevision() !== this.refreshedRevision) {
      this.refreshedToken = this.refreshToken; this.refreshedRevision = this.summaryRevision(); this.refreshPending = true;
    }
    if (this.refreshPending && !this.busy) { this.readOperation(true); }
  }
  private readOperation(replace = false): void {
    if (!this.allowed() || this.loading && !replace || this.busy || this.destroyed) { return; }
    if (this.request) { this.request.unsubscribe(); }
    this.loading = true; this.retryAllowed = false; this.refreshPending = false;
    const generation = ++this.generation;
    this.request = this.promotions.readOperation(this.employee(), this.operationKey())
      .subscribe(value => this.accept(value, generation, false), error => this.reject(error, generation, false));
  }
  private current(generation: number): boolean {
    return !this.destroyed && generation === this.generation && this.currentContext() === this.context;
  }
  private accept(value: GradePromotionResponse, generation: number, command: boolean): void {
    if (!this.current(generation)) { return; }
    if (!value || value.userId !== this.employee() || value.operationId !== this.operationKey() || !value.state) {
      this.reject({status: ZERO}, generation, command); return;
    }
    this.loading = false; this.busy = false; this.operation = value;
    this.outcomeUnknown = value.state === APPLYING; this.retryAllowed = false; this.errorKey = EMPTY; this.present();
    const key = JSON.stringify([this.employee(), this.operationKey()]);
    if (value.state === COMMITTED && this.committedOperations.indexOf(key) < ZERO) {
      this.committedOperations.push(key);
      this.promotions.notifyCommitted(this.event());
      this.committed.emit(this.event());
    } else if (value.state === CANCELLED || value.state === REJECTED) {
      this.refreshResolved();
    } else if (command && value.state === APPLYING) { this.readOperation(); }
    this.refreshOperation();
  }
  private reject(error: {status?: number; error?: {code?: string}}, generation: number, command: boolean): void {
    if (!this.current(generation)) { return; }
    const status = error && error.status || ZERO;
    this.loading = false; this.busy = false; this.operation = null;
    this.errorKey = growthPromotionErrorKey(status, error && error.error && error.error.code);
    if (command && (status === ZERO || status >= SERVER_ERROR && status !== UNAVAILABLE)) {
      this.outcomeUnknown = true; this.errorKey = UNKNOWN_OUTCOME; this.readOperation(); return;
    }
    if (command && status === CONFLICT) {
      this.noticeKey = this.errorKey;
      this.refreshRequested.emit(this.event()); this.readOperation(); return;
    }
    if (status === NOT_FOUND) { this.refreshResolved(); }
    this.retryAllowed = status !== UNAUTHORIZED && status !== INVALID && (status !== FORBIDDEN || this.outcomeUnknown);
    if (this.outcomeUnknown && status !== UNAUTHORIZED) { this.errorKey = UNKNOWN_OUTCOME; }
    if (status === UNAUTHORIZED) { this.outcomeUnknown = false; }
    this.present(); this.refreshOperation();
  }
  private event(): GradePromotionEvent { return {employeeId: this.employee(), operationId: this.operationKey()}; }
  private summaryRevision(): number {
    const summary = this.summary || this.evaluation && this.evaluation.activeOperation;
    return summary ? summary.revision : null;
  }
  private refreshResolved(): void {
    const key = JSON.stringify([this.employee(), this.operationKey()]);
    if (this.resolvedOperations.indexOf(key) >= ZERO) { return; }
    this.resolvedOperations.push(key); this.refreshRequested.emit(this.event());
  }
  private present(): void {
    const summary = this.summary || this.evaluation && this.evaluation.activeOperation;
    this.modeKey = this.headFlow ? EMPTY : growthAutomationKey(this.evaluation ? this.evaluation.automationMode : null);
    this.active = this.operation ? growthOperationView(this.operation) : growthPromotionView(summary, this.headFlow);
    this.compactFromKey = this.active && this.active.fromKey || dealPayrollGradeKey(<DealPayrollGradeCode>this.fromGradeCode);
    this.compactToKey = this.active && this.active.toKey || dealPayrollGradeKey(<DealPayrollGradeCode>this.toGradeCode);
    this.conditions = growthEvaluationView(this.operation ? this.operation.transitionEvaluation : null, this.learningPreview);
    const last = this.evaluation && this.evaluation.lastPromotion;
    this.lastCommitted = last && last.state === COMMITTED && (!this.active || last.operationId !== this.active.summary.operationId)
      ? growthPromotionView(last, this.headFlow) : null;
  }
  private cancel(): void {
    this.generation++; this.loading = false; this.busy = false; this.refreshPending = false;
    if (this.request) { this.request.unsubscribe(); this.request = null; }
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/grade-growth/grade-promotion.component.ts